import React, { useState } from 'react';
import { toast } from 'sonner';
import { ClipboardList, FileText, Package } from 'lucide-react';
import {
  groupOrdersByPos,
  orderLineQty,
  pickableOrders,
  type OpenOwnOrder,
} from '../../utils/pickListGrouping';
import { EditPosOrderSheet } from './EditPosOrderSheet';
import { OwnDeliveryStoreCard } from './OwnDeliveryStoreCard';
import type { AttachedDocumentRef } from '../../utils/documentToast';
import { useLongPressSelect } from '../../hooks/useLongPressSelect';
import { SelectionBar } from '../ui/SelectionBar';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { api } from '../../services/api.js';
import { extractApiError } from '../../utils/pdfDownload';

export interface PackingCard {
  id: string;
  order_count: number;
  pos_count: number;
}

interface OwnDeliveryPanelProps {
  packing: PackingCard[];
  orders: OpenOwnOrder[];
  onCreate: () => void;
  onContinue: (listId: string) => void;
  onMoved: (documents: AttachedDocumentRef[]) => void;
  onOrdersChanged: () => void;
}

function selectedLabels(orders: OpenOwnOrder[], ids: Set<string>): string {
  return orders
    .filter((o) => ids.has(o.id))
    .map(
      (o) =>
        `${orderLineQty(o)} ${o.product_name}${o.pos_name ? ` (${o.pos_name})` : ''}`
    )
    .join(', ');
}

export const OwnDeliveryPanel: React.FC<OwnDeliveryPanelProps> = ({
  packing,
  orders,
  onCreate,
  onContinue,
  onMoved,
  onOrdersChanged,
}) => {
  const groups = groupOrdersByPos(orders);
  const pickable = pickableOrders(orders);
  const empty = packing.length === 0 && orders.length === 0;
  const select = useLongPressSelect();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const editingGroup = groups.find((g) => g.key === editing);

  const handleDelete = async () => {
    const ids = [...select.selected];
    if (ids.length === 0) return;
    try {
      await api.post('/orders/bulk-delete', { ids });
      toast.success('Deleted');
      select.exit();
      onOrdersChanged();
    } catch (err) {
      toast.error(extractApiError(err).message);
      throw err;
    }
  };

  if (empty) {
    return (
      <div className="text-center py-20 bg-white rounded-[2rem] border-2 border-dashed border-gray-300">
        <FileText size={48} className="mx-auto text-gray-300 mb-3" />
        <p className="text-sm font-bold text-gray-600">No open Own Delivery orders</p>
        <p className="text-xs text-gray-400 mt-1">
          Save an order with Own Delivery to pack it here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {packing.map((card) => (
        <button
          key={card.id}
          type="button"
          onClick={() => onContinue(card.id)}
          className="w-full flex items-center gap-3 px-4 py-3.5 bg-white rounded-2xl border border-gray-200 text-left"
        >
          <Package size={18} className="text-gray-900 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-semibold text-gray-900 leading-5">
              Packing
            </p>
            <p className="text-xs text-gray-500 leading-4 mt-0.5">
              {card.pos_count} store{card.pos_count === 1 ? '' : 's'} ·{' '}
              {card.order_count} line{card.order_count === 1 ? '' : 's'}
            </p>
          </div>
          <span className="flex-shrink-0 h-11 px-3.5 rounded-xl border border-gray-200 bg-gray-100 text-sm font-semibold text-gray-900 flex items-center">
            Continue
          </span>
        </button>
      ))}
      {pickable.length > 0 && !select.mode ? (
        <button
          type="button"
          onClick={onCreate}
          className="w-full min-h-[48px] rounded-xl bg-brand-600 text-white font-bold text-sm flex items-center justify-center gap-2"
        >
          <ClipboardList size={16} />
          Create pick list
        </button>
      ) : null}
      {groups.map((group) => (
        <OwnDeliveryStoreCard
          key={group.key}
          group={group}
          selecting={select.mode}
          selected={select.selected}
          bindRow={select.bindRow}
          onEdit={
            select.mode || !group.posId
              ? undefined
              : () => setEditing(group.key)
          }
        />
      ))}
      {select.mode ? (
        <SelectionBar
          count={select.selected.size}
          onDone={select.exit}
          onDelete={() => setConfirmOpen(true)}
        />
      ) : null}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        variant="danger"
        title="Delete orders?"
        message={`This cannot be undone. ${selectedLabels(orders, select.selected)}`}
        confirmText="Delete"
        onConfirm={handleDelete}
      />
      {editingGroup ? (
        <EditPosOrderSheet
          group={editingGroup}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onOrdersChanged();
            onMoved([]);
          }}
        />
      ) : null}
    </div>
  );
};
